import React, { useEffect } from "react";
import { withTranslation } from "react-i18next";
import { Checkbox, Box, Button, Grid, Typography } from "@mui/material";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import PropTypes from "prop-types";

import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";
import YesNoRadioButton from "Components/RegisterBuildingForm/YesNoRadioButton";

import { onlyNumberKeys } from "utils";

const FireProtectionEquipment = ({ formik, t }) => {
  const { values, setFieldValue, setFieldTouched } = formik;

  const {
    fireSafetyAddress: { floors },
    floorWiseFireProtectionEquipments,
    fireProtection: { isPublicAddressingSystem },
  } = values;

  const handleAddNewFireProtectionEquipment = (
    existingfireProtectionEquipment
  ) => {
    const newFireProtectionEquipmentList = [
      ...existingfireProtectionEquipment,
      {
        id: 0,
        floors: [],
        hydrantValve: "",
        hoseBox: "",
        hosePipe: "",
        branchPipe: "",
        hoseReel: "",
        pumpOnOffSwitch: "",
        signages: "",
        fireExtinguishers: "",
        sprinklers: "",
      },
    ];
    setFieldValue(
      "floorWiseFireProtectionEquipments",
      newFireProtectionEquipmentList
    );
    setFieldTouched(
      `floorWiseFireProtectionEquipments[${
        newFireProtectionEquipmentList.length - 1
      }]`,
      false,
      false
    );
  };

  const handleRemoveFireProtectionEquipmentClick = (index) => {
    if (floorWiseFireProtectionEquipments.length > 1) {
      setFieldValue(
        "floorWiseFireProtectionEquipments",
        floorWiseFireProtectionEquipments.filter((_, ind) => ind !== index)
      );
    }
  };

  // creating initial values for fire protection equipment
  useEffect(() => {
    if (!floorWiseFireProtectionEquipments.length) {
      setFieldValue("floorWiseFireProtectionEquipments", [
        {
          id: 0,
          floors: [],
          hydrantValve: "",
          hoseBox: "",
          hosePipe: "",
          branchPipe: "",
          hoseReel: "",
          pumpOnOffSwitch: "",
          signages: "",
          fireExtinguishers: "",
          sprinklers: "",
        },
      ]);
    }
  }, [floorWiseFireProtectionEquipments]);

  return (
    <Grid container columnSpacing={1} rowSpacing={1}>
      <Grid item xs={12} className="border ps-3 pe-2 pb-2 pt-0 pt-3">
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
          <Box>
            <Typography className="mb-3 me-3" lineHeight={1} variant="h6">
              {t("Fire Protection Equipment")}
            </Typography>
          </Box>
          <Box>
            <Button
              color="primary"
              className="mb-3"
              sx={{ minWidth: "unset", textTransform: "capitalize" }}
              variant="outlined"
              onClick={() =>
                handleAddNewFireProtectionEquipment(
                  floorWiseFireProtectionEquipments
                )
              }
            >
              <strong>
                <span>+</span> {t("Add Fire Protection Equipment")}
              </strong>
            </Button>
          </Box>
        </Box>

        {!!floorWiseFireProtectionEquipments.length &&
          floorWiseFireProtectionEquipments.map(
            (
              {
                floors: protectionEquipmentFloors,
                hydrantValve,
                hoseBox,
                hosePipe,
                hoseReel,
                pumpOnOffSwitch,
                signages,
                fireExtinguishers,
                sprinklers,
                branchPipe,
              },
              index
            ) => (
              <Grid
                key={index}
                container
                columnSpacing={1}
                rowSpacing={1}
                className="border p-2 pe-3 mt-2"
              >
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <SelectField
                    label={t("Floors")}
                    name={`floorWiseFireProtectionEquipments.${index}.floors`}
                    formik={formik}
                    options={floors}
                    placeholder="Select Floor Number"
                    multiple
                    disableCloseOnSelect
                    isOptionEqualToValue={(option, { value }) =>
                      option.value === value
                    }
                    renderOption={(props, option, { selected }) => {
                      return (
                        <li {...props} key={option.value}>
                          <Checkbox
                            sx={{ marginRight: "8px" }}
                            checked={selected}
                          />
                          {option.label}
                        </li>
                      );
                    }}
                    onChange={(event, options) => {
                      if (event.key !== "Backspace" && event.key !== "Delete") {
                        setFieldValue(
                          `floorWiseFireProtectionEquipments.${index}.floors`,
                          options
                        );
                      }
                    }}
                    value={protectionEquipmentFloors || []}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <InputFieldText
                    label={t("Hydrant Valve") + "*"}
                    name={`floorWiseFireProtectionEquipments.${index}.hydrantValve`}
                    formik={formik}
                    placeholder="Enter Hydrant Valve"
                    type="number"
                    value={hydrantValve ?? ""}
                    disabled={!floors.length}
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <InputFieldText
                    label={t("Hose Box") + "*"}
                    name={`floorWiseFireProtectionEquipments.${index}.hoseBox`}
                    formik={formik}
                    placeholder="Enter Hose Box"
                    type="number"
                    value={hoseBox ?? ""}
                    disabled={!floors.length}
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <InputFieldText
                    label={t("Hose Pipe") + "*"}
                    name={`floorWiseFireProtectionEquipments.${index}.hosePipe`}
                    formik={formik}
                    placeholder="Enter Hose Pipe"
                    type="number"
                    value={hosePipe ?? ""}
                    disabled={!floors.length}
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <InputFieldText
                    label={t("Branch Pipe") + "*"}
                    name={`floorWiseFireProtectionEquipments.${index}.branchPipe`}
                    formik={formik}
                    placeholder="Enter Branch Pipe"
                    type="number"
                    value={branchPipe ?? ""}
                    disabled={!floors.length}
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <InputFieldText
                    label={t("Hose Reel Hose") + "*"}
                    name={`floorWiseFireProtectionEquipments.${index}.hoseReel`}
                    formik={formik}
                    placeholder="Enter Hose Reel Hose"
                    type="number"
                    value={hoseReel ?? ""}
                    disabled={!floors.length}
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <InputFieldText
                    label={t("Pump On/Off Switch") + "*"}
                    name={`floorWiseFireProtectionEquipments.${index}.pumpOnOffSwitch`}
                    formik={formik}
                    placeholder="Enter Pump On/Off Switch"
                    type="number"
                    value={pumpOnOffSwitch ?? ""}
                    disabled={!floors.length}
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <InputFieldText
                    label={t("Signages") + "*"}
                    name={`floorWiseFireProtectionEquipments.${index}.signages`}
                    formik={formik}
                    placeholder="Enter Signages"
                    type="number"
                    value={signages ?? ""}
                    disabled={!floors.length}
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <InputFieldText
                    label={t("Fire Extinguishers") + "*"}
                    name={`floorWiseFireProtectionEquipments.${index}.fireExtinguishers`}
                    formik={formik}
                    placeholder="Enter Fire Extinguishers"
                    type="number"
                    value={fireExtinguishers ?? ""}
                    disabled={!floors.length}
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <InputFieldText
                    label={t("Sprinklers") + "*"}
                    name={`floorWiseFireProtectionEquipments.${index}.sprinklers`}
                    formik={formik}
                    placeholder="Enter Sprinklers"
                    type="number"
                    value={sprinklers ?? ""}
                    disabled={!floors.length}
                    InputProps={{
                      inputProps: {
                        min: 0,
                        onKeyDown: (e) => onlyNumberKeys(e, true),
                      },
                    }}
                  />
                </Grid>

                {floorWiseFireProtectionEquipments.length > 1 && (
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <Button
                      color="error"
                      size="small"
                      sx={{
                        minWidth: "unset",
                        border: "1px solid",
                        height: "40px",
                        width: "100px",
                        mb: 2,
                      }}
                      onClick={() =>
                        handleRemoveFireProtectionEquipmentClick(index)
                      }
                    >
                      <strong className="d-flex justify-content-center">
                        <DeleteOutlineSharpIcon fontSize="small" />
                        {t("Remove")}
                      </strong>
                    </Button>
                  </Grid>
                )}
              </Grid>
            )
          )}
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <YesNoRadioButton
          label="Evacuation plan"
          keyName="fireProtection.isEvacuationPlan"
          formik={formik}
          isRequired
        />
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <YesNoRadioButton
          label="Public addressing system"
          keyName="fireProtection.isPublicAddressingSystem"
          formik={formik}
          isRequired
          onChange={(e) => {
            if (!+e.target.value) {
              setFieldValue(
                "fireProtection.isPublicAddressingSystemInTenantArea",
                ""
              );
            }
            setFieldValue(
              "fireProtection.isPublicAddressingSystem",
              !!+e.target.value
            );
          }}
        />
      </Grid>
      {!!isPublicAddressingSystem && (
        <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
          <YesNoRadioButton
            label="Public addressing system provided in tenant area"
            keyName="fireProtection.isPublicAddressingSystemInTenantArea"
            formik={formik}
            isRequired
          />
        </Grid>
      )}
      <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
        <YesNoRadioButton
          label="External Lifts"
          keyName="fireProtection.isExternalLifts"
          formik={formik}
          isRequired
        />
      </Grid>
    </Grid>
  );
};

FireProtectionEquipment.propTypes = {
  formik: PropTypes.object,
  t: PropTypes.func,
};

export default withTranslation()(FireProtectionEquipment);
